import styled from '@emotion/styled';
import { AdAnalyticsReport } from '@kaizenplatform/hugot-entity';
import { Button, Modal, Paper, Tabs, Typography } from '@material-ui/core';
import { List } from 'immutable';
import { createElement, FC, Fragment, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import AvatarAndName from '../../component/compound/AvatarAndName';
import Spinner from '../../component/compound/Spinner';
import Tab from '../../component/primitive/Tab';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_HUGE, SPACING_LARGE, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer } from '../../styleUtility/gridLayout';

type Props = {
  className?: string;
  analyticsReports: List<AdAnalyticsReport>;
  loading: boolean;
  deleteReport: (id: string) => void;
  onGotoReportDetailPage: (id: string) => void;
  onGotoAnalyticsListPage: () => void;
  onMount: () => void;
  onUnmount: () => void;
};

const ShowAdAnalyticsReportListPage: FC<Props> = ({
  className,
  analyticsReports,
  loading,
  deleteReport,
  onGotoReportDetailPage,
  onGotoAnalyticsListPage,
  onMount,
  onUnmount,
}: {
  className?: string;
  analyticsReports: List<AdAnalyticsReport>;
  loading: boolean;
  deleteReport: (id: string) => void;
  onGotoReportDetailPage: (id: string) => void;
  onGotoAnalyticsListPage: () => void;
  onMount: () => void;
  onUnmount: () => void;
}) => {
  useEffect(() => {
    onMount();
    return () => {
      onUnmount();
    };
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const activePage = 'report';

  const [modalOpen, updateModalOpen] = useState(false);
  const [deleteTarget, updateDeleteTarget] = useState('');

  const handleOpenModal = (id: string) => {
    updateModalOpen(true);
    updateDeleteTarget(id);
  };

  const handleCloseModal = () => {
    updateModalOpen(false);
  };

  const handleSubmitDelete = () => {
    updateModalOpen(false);
    deleteReport(deleteTarget);
  };

  return (
    <Root className={className}>
      <Title variant="h5">
        <FormattedMessage id="adAnalytics_report_list.page_title" />
      </Title>
      <Tabs value={activePage} indicatorColor="primary" textColor="primary">
        <Tab
          label={<FormattedMessage id="adAnalytics_report_list.tab_creative" />}
          value="creative"
          onClick={() => onGotoAnalyticsListPage()}
        />
        <Tab label={<FormattedMessage id="adAnalytics_report_list.tab_report" />} value="report" />
      </Tabs>
      <Container>
        {loading ? (
          <Loading>
            <Spinner />
          </Loading>
        ) : (
          <>
            {analyticsReports
              ? analyticsReports.map((report) => (
                  <Report key={report.id} className={className} onClick={() => onGotoReportDetailPage(report.id)}>
                    <ReportTitle>{report.title}</ReportTitle>
                    <ReportUserInfo>
                      <AvatarAndName user={report.createdBy} size={20} variant="caption" />
                    </ReportUserInfo>
                    <ReportDate>
                      <FormattedDate
                        value={report.createdAt.toJSDate()}
                        year="numeric"
                        month="2-digit"
                        day="2-digit"
                        hour="2-digit"
                        minute="2-digit"
                      />
                    </ReportDate>
                    <ReportDeleteButton
                      color="primary"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(report.id);
                      }}
                    >
                      <FormattedMessage id="adAnalytics_report_list.button_delete" />
                    </ReportDeleteButton>
                  </Report>
                ))
              : null}
          </>
        )}
      </Container>
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <ModalStyle>
          <ModalTitle>
            <FormattedMessage id="adAnalytics_report_list.modal_label" />
          </ModalTitle>
          <Button
            color="primary"
            variant="contained"
            onClick={(_e) => {
              handleSubmitDelete();
            }}
            fullWidth={true}
          >
            <FormattedMessage id="adAnalytics_report_list.modal_button" />
          </Button>
        </ModalStyle>
      </Modal>
    </Root>
  );
};

const Root = styled('section')({
  maxWidth: SCREEN_LARGE,
  margin: `0 auto ${SPACING_UNIT * 10}px`,
  paddingTop: `${SPACING_HUGE}px`,
});

const Title = styled(Typography)({
  marginBottom: SPACING_LARGE,
});

const Container = styled(Paper)(
  gridLayoutContainer({
    columns: ['100%'],
    rows: ['auto'],
    areas: [['filter'], ['analytics']],
    alignItems: 'center',
  }),
  {
    width: 'auto',
  },
);

const Report = styled('section')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
  borderBottom: '1px solid #eeeeee',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F9FAFD',
  },
});

const ReportTitle = styled('div')({
  color: 'rgba(0, 0, 0, 0.87)',
  fontSize: '18px',
  fontWeight: 700,
  width: '50%',
  flexWrap: 'wrap',
});

const ReportUserInfo = styled('div')({
  display: 'flex',
  marginRight: '16px',
});

const ReportDate = styled('div')({
  display: 'flex',
  color: 'rgba(0, 0, 0, 0.53)',
  fontSize: '14px',
});

const ReportDeleteButton = styled(Button)({
  height: '24px',
  marginLeft: '16px',
});

const ModalStyle = styled('div')({
  // シャドウと角丸つける
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '450',
  background: 'white',
  outline: 'none',
  boxShadow: '0 3px 7px rgba(0, 0, 0, 0.3)',
  borderRadius: '10px',
  border: '1px solid rgba(0, 0, 0, 0.3)',
  padding: '16px',
});

const ModalTitle = styled('div')({
  fontSize: '18px',
  color: '#222',
  margin: '32px 0px',
});

const Loading = styled('div')({
  padding: SPACING_HUGE,
});

export default ShowAdAnalyticsReportListPage;
